import { useEffect, useState, useMemo } from "react";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { buscarContasSpedListaPorCnpj } from "../../services/api-relatorios.service";
import { Autocomplete, TextField, Chip, Box } from "@mui/material";

const SELECIONAR_TODOS = { id: "__SELECIONAR_TODOS__", detalhes: "Selecionar Todos" };

const ContasContabeis = ({ schemas, setFiltros }) => {
  const [contas, setContas] = useState([]);
  const [contasSelecionadas, setContasSelecionadas] = useState([]);
  const [loading, setLoading] = useState(false);

  /** Primeiro CNPJ da seleção de empresas (modal com schema). */
  const cnpjAnchor = useMemo(() => {
    const id = schemas?.[0]?.id;
    if (id == null) return "";
    const d = String(id).replace(/\D/g, "");
    return d.length >= 11 && d.length <= 14 ? d : "";
  }, [schemas]);

  const baseFilter = createFilterOptions({ stringify: (option) => option?.detalhes || "" });
  const keepSentinelsFirst = (options, params) => {
    const filtered = baseFilter(options, params);
    const isSentinel = (o) => o?.id === "__SELECIONAR_TODOS__";
    const sentinels = options.filter(isSentinel);
    const rest = filtered.filter((o) => !isSentinel(o));
    return [...sentinels, ...rest];
  };

  const montaOptions = async (cnpj) => {
    setLoading(true);
    try {
      const res = await buscarContasSpedListaPorCnpj(cnpj);
      const list = Array.isArray(res?.contas) ? res.contas : [];
      return list.filter((c) => c?.id);
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cnpjAnchor) {
      if (contas.length !== 0) setContas([]);
      if (contasSelecionadas.length !== 0) setContasSelecionadas([]);
      setFiltros((prev) =>
        prev.map((opt) =>
          opt.nome === "Contas Contábeis" ? { ...opt, valor: [] } : opt
        )
      );
      return;
    }

    const fetchData = async () => {
      const optionListaContas = await montaOptions(cnpjAnchor);
      setContas([SELECIONAR_TODOS, ...optionListaContas]);
      setContasSelecionadas([]);
      setFiltros((prev) =>
        prev.map((opt) =>
          opt.nome === "Contas Contábeis" ? { ...opt, valor: [] } : opt
        )
      );
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpjAnchor]);

  const handleAutocompleteChange = (value) => {
    if (!value) {
      setContasSelecionadas([]);
      setFiltros((prevFiltros) =>
        prevFiltros.map((option) =>
          option.nome === "Contas Contábeis" ? { ...option, valor: [] } : option
        )
      );
      return;
    }

    const selecionarTodosEstaPresente = value.some((item) => item.id === "__SELECIONAR_TODOS__");
    const contasReais = contas.filter((conta) => conta.id !== "__SELECIONAR_TODOS__");

    if (selecionarTodosEstaPresente) {
      const todosSelecionados = contasSelecionadas.length === contasReais.length;

      if (todosSelecionados) {
        setContasSelecionadas([]);
        setFiltros((prevFiltros) =>
          prevFiltros.map((option) =>
            option.nome === "Contas Contábeis" ? { ...option, valor: [] } : option
          )
        );
      } else {
        setContasSelecionadas([SELECIONAR_TODOS, ...contasReais]);
        const novasContas = contasReais.map((item) => item.id);
        setFiltros((prevFiltros) =>
          prevFiltros.map((option) =>
            option.nome === "Contas Contábeis" ? { ...option, valor: novasContas } : option
          )
        );
      }
    } else {
      const contasSelecionadasReais = value.filter((item) => item.id !== "__SELECIONAR_TODOS__");
      const todasSelecionadas = contasSelecionadasReais.length === contasReais.length;

      if (todasSelecionadas) {
        setContasSelecionadas([SELECIONAR_TODOS, ...contasSelecionadasReais]);
      } else {
        setContasSelecionadas(contasSelecionadasReais);
      }

      const novasContas = contasSelecionadasReais.map((item) => item.id);
      setFiltros((prevFiltros) =>
        prevFiltros.map((option) =>
          option.nome === "Contas Contábeis" ? { ...option, valor: novasContas } : option
        )
      );
    }
  };

  const renderTags = (tagValue, getTagProps) => {
    const contasReais = tagValue.filter((conta) => conta.id !== "__SELECIONAR_TODOS__");

    if (contasReais.length > 5) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 80, overflow: "auto" }}>
          {contasReais.slice(0, 3).map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.id} label={option.detalhes} size="small" />
          ))}
          <Chip label={`+${contasReais.length - 3} mais`} size="small" variant="outlined" />
        </Box>
      );
    }

    return contasReais.map((option, index) => (
      <Chip {...getTagProps({ index })} key={option.id} label={option.detalhes} size="small" />
    ));
  };

  return (
    <div>
      <Autocomplete
        multiple
        loading={loading}
        options={contas}
        value={contasSelecionadas}
        disableCloseOnSelect
        filterOptions={keepSentinelsFirst}
        getOptionLabel={(option) => option.detalhes}
        noOptionsText="Nenhuma conta encontrada."
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ marginTop: "1rem", width: "100%" }}
        onChange={(_, value) => handleAutocompleteChange(value)}
        renderTags={renderTags}
        renderInput={(params) => <TextField {...params} label="Contas Contábeis" />}
      />
    </div>
  );
};

export default ContasContabeis;
